//* admin 전용 주문 상품 교체 모달 — 결제 후 상품을 바꾸는 정정 처리. 고른 상품의 판매가에 따라 두 갈래:
//*  - 상향·동가: 차액을 계좌 등으로 **따로 받은 뒤** 기록한다 (change-item, docs/shop-order-change-item-spec.md)
//*  - 하향: 차액을 **토스 부분취소로 즉시 환불**한다 (change-item-refund, docs/shop-order-change-item-refund-spec.md)
//*    차액은 프론트가 보내지 않고 서버가 상품가로 계산한다. 환불 경로도 담당자가 고르지 않고
//*    결제수단이 정한다 — 화면은 판정 결과(카드/간편결제=원결제수단, 가상계좌=환불계좌 입력)만 보여준다.
//* 후보 상품은 주문의 bugo 기준 판매가능 목록(bugoBrand rebate + 유통망 매칭) 중 같은 shop 것만.
//* 비활성(isEnabled=false) 상품 포함은 의도: 고객에게 숨긴 지역별 고가 변형을 교체에 쓰기 위함.
//* (404 안내는 구서버 안전장치)

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { adminShopItemListByBugoForShopUrl } from '@shared/api/shopItem/shopItem.hook';
import { ShopItem } from '@shared/api/shopItem/shopItem.interface';
import { ShopItemInfo } from '@shared/api/shopItemInfo/shopItemInfo.interface';
import { useRefundBankList } from '@shared/api/shopOrder/refundBank.hook';
import {
  adminChangeShopOrderItem,
  adminChangeShopOrderItemWithRefund,
  ShopOrderItemChange,
  ShopOrderItemChangeRefund,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import {
  isTossVbankPayment,
  partialCancelBlockReason,
  shopOrderPgBalance,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import Modal from '@shared/components/Modal';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

type CandidateItem = ShopItem<ShopItemInfo>;

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const serverMessageOf = (e: unknown): string | undefined =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message;

const changeErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 404:
      return '주문/상품을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 400:
      return '교체할 수 없는 상품이거나 요청이 올바르지 않습니다.';
    case 403:
      return '관리자만 사용할 수 있는 기능입니다.';
    case 409:
      return '주문 상태가 방금 바뀌어(취소·정산 등) 교체하지 못했습니다. 새로고침 후 다시 확인해주세요.';
    default:
      return '상품 교체에 실패했습니다.';
  }
};

//* 하향 교체(부분취소)는 서버가 400에 우리 말 message를 실어 보낸다(NOT_A_DOWNGRADE·잔액 초과·
//* 환불계좌 오류·토스 거절 사유). 전역 필터가 error 코드를 exception.name으로 덮어쓰므로
//* 코드 분기는 못 하고 message를 그대로 보여준다. 409는 중복 클릭 잠금 또는 PG 성공 후 DB 반영
//* 실패(REFUND_UPDATE_FAILED) — 둘 다 "다시 누르면 이중환불"이라 새로고침 안내가 맞다.
const refundErrorMessage = (e: unknown): string => {
  const status = statusOf(e);
  const message = serverMessageOf(e);
  if (status === 400 && message) return message;
  switch (status) {
    case 404:
      return '주문/상품을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다(하향 교체 부분취소 미배포). 수기환불로 처리해주세요.';
    case 403:
      return '관리자만 사용할 수 있는 기능입니다.';
    case 409:
      return (
        message ??
        '이미 차액 환불이 처리 중이거나 주문 상태가 바뀌었습니다. 다시 누르지 말고 새로고침해 결과를 확인해주세요.'
      );
    default:
      return '차액 환불(부분취소)에 실패했습니다. 다시 누르기 전에 새로고침해 환불이 나갔는지 확인해주세요.';
  }
};

//* 도매가 2종 — 도매표기(shopItemInfo.priceWhole): 57,500 대신 60,000처럼 헷갈림 방지용
//* 반올림 표기값. 실도매(shopItem.priceWhole): 정산·장부에 실제 쓰는 값(없으면 표기값) —
//* embed 매핑(priceWhole = shopItem ?? shopItemInfo)과 동일 규칙.
const displayWholesaleOf = (item: CandidateItem): number | undefined =>
  item.shopItemInfo?.priceWhole;
const actualWholesaleOf = (item: CandidateItem): number | undefined =>
  item.priceWhole ?? item.shopItemInfo?.priceWhole;

const won = (n: number | undefined | null): string =>
  typeof n === 'number' ? `${n.toLocaleString()}원` : '-';

export const OrderItemChangeModal: FC<Props> = function OrderItemChangeModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const currentShopItem = order.orderDetail.shopItem;
  const currentEmbed = order.orderDetail.shopItemEmbed;
  const quantity = order.orderDetail.quantity ?? 1;
  //* 후보 목록의 출처(부고브랜드 유통망) 표기용 — 예: 프리드
  const brandName = order.bugo?.bugoBrand?.bugoAgency?.nickName;
  //* 현재까지 받은 금액 (현금 + 포인트)
  const paymentByCash = order.paymentDetail.paymentByCash ?? 0;
  const paidTotal = paymentByCash + (order.paymentDetail.paymentByPoint ?? 0);
  //* 현재 상품가 합계 — 하향 교체 차액은 결제액이 아니라 이 값(embed.priceRetail × 수량) 기준(서버와 동일)
  const currentItemTotal = (currentEmbed?.priceRetail ?? 0) * quantity;
  const isVbank = isTossVbankPayment(order.paymentDetail);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  //* 교체 후보 상품 목록 (모달 열릴 때 조회)
  const [items, setItems] = useState<CandidateItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  //* ── 상향·동가 모드 입력 ──
  const [additionalPayment, setAdditionalPayment] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  //* 계좌로 받은 차액의 현금영수증 — 입금확인이 곧 교체 시점이라 그 자리에서 발행하는
  //* 흐름이 자연스럽다. 기본값은 끄기: 실제로 발행하지 않았는데 도장부터 찍히면
  //* 큐에서 사라져 영영 누락된다(반대는 큐에 남을 뿐이라 회복된다).
  const [cashReceiptIssued, setCashReceiptIssued] = useState(false);
  //* ── 하향(부분취소) 모드 입력 ──
  const [reason, setReason] = useState<string>('');
  const [bank, setBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  //* 돈이 즉시 나가고 되돌릴 수 없어 금액을 한 번 더 읽게 한다
  const [refundConfirmed, setRefundConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  //* 가상계좌 건일 때만 은행 목록 조회 (구서버 404면 프론트 폴백 목록으로 대체된다)
  const bankList = useRefundBankList(open && isVbank);

  const bugoId = order.bugo?._id;
  const shopId = typeof order.shop === 'string' ? order.shop : undefined;

  //* 열리는 순간에만 초기화한다. order를 deps에 넣으면 편집 중 창 포커스 refetch로 입력값이 사라진다.
  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setAdditionalPayment('');
    setMemo('');
    setCashReceiptIssued(false);
    setReason('');
    setBank('');
    setAccountNumber('');
    //* 예금주는 주문자 이름으로 채워두되 수정 가능 — 통장 명의가 다른 경우가 흔하다
    setHolderName(order.user?.info?.name ?? order.deliveryDetail?.senderName ?? '');
    setRefundConfirmed(false);
    if (!bugoId || !shopId) {
      setListError('부고/꽃집 정보가 없는 주문은 상품 교체를 지원하지 않습니다.');
      return;
    }
    setItems(null);
    setListError(null);
    api_
      .get<CandidateItem[]>(adminShopItemListByBugoForShopUrl(bugoId, shopId))
      .then(({ data }) => setItems(data))
      .catch((err) => {
        console.error(err);
        setListError('판매가능 상품 목록을 불러오지 못했습니다.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bugoId, shopId]);

  const candidates = useMemo(
    () => (items ?? []).filter((item) => item._id !== currentShopItem?._id),
    [items, currentShopItem?._id],
  );

  const selectedItem = useMemo(
    () => candidates.find((item) => item._id === selectedId) ?? null,
    [candidates, selectedId],
  );

  //* 상품 선택 시 차액 기본값 = 새 판매가 × 수량 − 현재 결제합계
  const onSelectItem = (item: CandidateItem) => {
    setSelectedId(item._id);
    setAdditionalPayment(String(Math.max(0, item.priceRetail * quantity - paidTotal)));
    setRefundConfirmed(false);
  };

  //* 모드 판정은 서버의 NOT_A_DOWNGRADE 규칙과 같이 **상품가** 비교로 한다 (결제액 아님)
  const selectedItemTotal = selectedItem ? selectedItem.priceRetail * quantity : 0;
  const refundAmount = selectedItem ? currentItemTotal - selectedItemTotal : 0;
  const isDowngrade = selectedItem !== null && refundAmount > 0;
  const refundBlockReason = isDowngrade
    ? partialCancelBlockReason(order, refundAmount)
    : null;
  const pgBalance = shopOrderPgBalance(order);

  //* 상향·동가 모드 계산
  const expectedDiff = selectedItem ? selectedItemTotal - paidTotal : 0;
  const additionalPaymentNumber = Number(additionalPayment || 0);
  const diffMismatch =
    selectedItem !== null && !isDowngrade && additionalPaymentNumber !== expectedDiff;

  const onSubmitUpgrade = useCallback(async () => {
    if (!selectedItem) return;
    if (!Number.isInteger(additionalPaymentNumber) || additionalPaymentNumber < 0) {
      notificateError(notifiacationInstance, '추가 수취금액은 0 이상의 정수여야 합니다.');
      return;
    }
    setSubmitting(true);
    try {
      const dto: ShopOrderItemChange = {
        shopItemId: selectedItem._id,
        additionalPaymentByCash: additionalPaymentNumber,
        memo: memo.trim() || undefined,
        //* 차액 0원이면 발행할 게 없다 — 서버도 무시하지만 보내지 않는 편이 명확하다
        ...(cashReceiptIssued && additionalPaymentNumber > 0
          ? { cashReceiptIssued: true }
          : {}),
      };
      await adminChangeShopOrderItem(order._id, dto);
      queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
      notificateSuccess(notifiacationInstance, '상품 교체 완료');
      onCloseModal();
    } catch (err) {
      console.error(err);
      notificateError(notifiacationInstance, changeErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedItem,
    additionalPaymentNumber,
    memo,
    cashReceiptIssued,
    order._id,
    queryClient,
    sellerRole,
    notifiacationInstance,
    onCloseModal,
  ]);

  const onSubmitRefund = useCallback(async () => {
    if (!selectedItem || !isDowngrade) return;
    if (refundBlockReason) {
      notificateError(notifiacationInstance, refundBlockReason);
      return;
    }
    if (!reason.trim()) {
      notificateError(notifiacationInstance, '교체 사유를 입력해주세요.');
      return;
    }
    if (isVbank) {
      if (!bank || !accountNumber.trim() || !holderName.trim()) {
        notificateError(
          notifiacationInstance,
          '환불계좌(은행·계좌번호·예금주)를 모두 입력해주세요.',
        );
        return;
      }
      //* 서버가 하이픈·공백은 지워주지만, 그 외 문자가 섞이면 토스가 거절한다 — 보내기 전에 잡는다
      if (/[^0-9]/.test(accountNumber.replace(/[\s-]/g, ''))) {
        notificateError(notifiacationInstance, '계좌번호는 숫자와 - 만 입력해주세요.');
        return;
      }
    }
    if (!refundConfirmed) {
      notificateError(notifiacationInstance, '환불 금액 확인에 체크해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      const dto: ShopOrderItemChangeRefund = {
        shopItemId: selectedItem._id,
        reason: reason.trim(),
        ...(isVbank
          ? {
              refundReceiveAccount: {
                bank,
                accountNumber: accountNumber.trim(),
                holderName: holderName.trim(),
              },
            }
          : {}),
      };
      await adminChangeShopOrderItemWithRefund(order._id, dto);
      //* 배송관리 목록·과거배송검색 등 주문 조회를 한 번에 갱신 (문자열 키는 prefix 매칭이 안 된다)
      await invalidateQueriesByUrlPart(queryClient, 'shop-order');
      //! 가상계좌는 "환불 완료"라고 쓰면 안 된다 — 토스에 입금완료 웹훅이 없다
      notificateSuccess(
        notifiacationInstance,
        isVbank
          ? `상품 교체 완료 · 차액 ${refundAmount.toLocaleString()}원 환불이 접수되었습니다. 입금까지 영업일 기준 1~2일 걸릴 수 있습니다.`
          : `상품 교체 완료 · 차액 ${refundAmount.toLocaleString()}원이 결제수단으로 환불됩니다(카드사 기준 3~7일).`,
      );
      onCloseModal();
    } catch (err) {
      console.error(err);
      notificateError(notifiacationInstance, refundErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedItem,
    isDowngrade,
    refundBlockReason,
    reason,
    isVbank,
    bank,
    accountNumber,
    holderName,
    refundConfirmed,
    refundAmount,
    order._id,
    queryClient,
    notifiacationInstance,
    onCloseModal,
  ]);

  const onSubmit = useCallback(() => {
    if (!selectedItem) {
      notificateError(notifiacationInstance, '교체할 상품을 선택해주세요.');
      return;
    }
    if (isDowngrade) onSubmitRefund();
    else onSubmitUpgrade();
  }, [selectedItem, isDowngrade, onSubmitRefund, onSubmitUpgrade, notifiacationInstance]);

  const submitDisabled =
    submitting ||
    !selectedItem ||
    (isDowngrade && (!!refundBlockReason || !refundConfirmed));

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">주문 상품 교체 (관리자)</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            결제 후 상품을 바꾸는 정정 처리입니다. 주문의 상품·가격정보(정산/리베이트 기준
            포함)와 결제금액이 함께 변경됩니다. <b>더 비싼 상품</b>은 차액을 따로 받은 뒤
            기록하고, <b>더 싼 상품</b>은 차액이 토스 부분취소로 즉시 환불됩니다.
          </p>
        </div>

        {/* 현재 상품 */}
        <div className="rounded-md border bg-gray-50 p-3 text-sm">
          <p className="font-medium">
            현재: {currentEmbed?.name ?? currentShopItem?.shopItemInfo?.name}
          </p>
          <p className="text-gray-700">
            판매가 {won(currentEmbed?.priceRetail ?? 0)} · 결제합계 {won(paidTotal)}
            {quantity > 1 ? ` · 수량 ${quantity}` : ''}
          </p>
          <p className="text-gray-700">
            도매표기 {won(currentShopItem?.shopItemInfo?.priceWhole)} · 실도매{' '}
            {won(currentEmbed?.priceWhole)}
            <span className="text-xs text-gray-500"> (정산 기준)</span>
          </p>
          <p className="text-xs text-gray-500">
            결제수단 {order.paymentDetail.method ?? '-'}
            {order.paymentDetail.provider ? ` · ${order.paymentDetail.provider}` : ''}
            {pgBalance !== paymentByCash ? ` · PG 잔액 ${won(pgBalance)}` : ''}
          </p>
        </div>

        {/* 교체 후보 목록 */}
        <div className="space-y-2">
          <p className="text-sm font-bold">
            교체할 상품 선택
            {brandName && (
              <span className="pl-1 font-normal text-cyan-700">
                — {brandName} 연결상품
              </span>
            )}
          </p>
          <p className="break-keep text-xs text-gray-500">
            {brandName ? `${brandName} 부고브랜드` : '주문 부고브랜드'}의 유통망에 연결된
            판매상품 중 같은 꽃집 것만 표시됩니다. 고객에게 숨긴 비활성 상품(지역별 가격
            변형)도 포함됩니다. 도매표기 = 헷갈림 방지용 반올림 표기값, 실도매 =
            정산·장부에 쓰는 실제 도매가.
          </p>
          {listError ? (
            <p className="text-sm text-red-600">{listError}</p>
          ) : items === null ? (
            <Loading />
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-500">교체 가능한 다른 상품이 없습니다.</p>
          ) : (
            <div className="max-h-60 space-y-1 overflow-y-auto">
              {candidates.map((item) => {
                const selected = item._id === selectedId;
                const cheaper = item.priceRetail * quantity < currentItemTotal;
                return (
                  <button
                    key={item._id}
                    type="button"
                    className={`flex w-full items-center justify-between rounded-md border p-2 text-left text-sm ${
                      selected
                        ? 'border-cyan-600 bg-cyan-50 font-bold'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => onSelectItem(item)}
                  >
                    <span className="truncate pr-2">
                      {item.name ?? item.shopItemInfo?.name}
                      {cheaper && (
                        <span className="ml-1 rounded bg-orange-100 px-1 text-[11px] font-normal text-orange-700">
                          하향·부분취소
                        </span>
                      )}
                    </span>
                    <span className="whitespace-nowrap text-right">
                      <span className="block">판매 {won(item.priceRetail)}</span>
                      <span
                        className={`block text-xs font-normal ${
                          selected ? 'text-cyan-700' : 'text-gray-500'
                        }`}
                      >
                        도매표기 {won(displayWholesaleOf(item))} · 실도매{' '}
                        {won(actualWholesaleOf(item))}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 상향·동가: 차액 수취 기록 */}
        {selectedItem && !isDowngrade && (
          <div className="space-y-2 rounded-md border p-3 text-sm">
            <p>
              새 판매가 {selectedItemTotal.toLocaleString()}원 − 결제합계{' '}
              {paidTotal.toLocaleString()}원 ={' '}
              <span className="font-bold">차액 {expectedDiff.toLocaleString()}원</span>
            </p>
            <p className="text-xs text-gray-600">
              실도매(정산 기준) {won(currentEmbed?.priceWhole)} →{' '}
              {won(actualWholesaleOf(selectedItem))} — 꽃집 정산 기준도 함께 변경됩니다.
            </p>
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap font-medium">추가 수취금액</label>
              <input
                type="number"
                min={0}
                className="w-32 rounded border border-gray-300 px-2 py-1 text-right"
                value={additionalPayment}
                onChange={(e) => setAdditionalPayment(e.target.value)}
              />
              <span>원</span>
            </div>
            {diffMismatch && (
              <p className="break-keep text-xs text-amber-600">
                계산된 차액({expectedDiff.toLocaleString()}원)과 다릅니다. 의도한 금액인지
                확인해주세요.
              </p>
            )}
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-2 py-1"
              placeholder="메모 (ex. 가상계좌 이체 35,000원 수취)"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
            {/* 계좌로 받은 차액은 PG가 발행해주지 않는다 — 여기서 처리했으면 도장까지,
                아니면 그냥 두면 현금영수증 발행 대기 큐에 남아 나중에 일괄 처리된다 */}
            {additionalPaymentNumber > 0 && (
              <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={cashReceiptIssued}
                  onChange={(e) => setCashReceiptIssued(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="break-keep">
                  현금영수증도 지금 발행함 (홈택스에서 차액{' '}
                  {additionalPaymentNumber.toLocaleString()}원을 발행한 경우에만 체크)
                  <span className="mt-0.5 block text-[11px] text-gray-400">
                    체크하지 않으면 현금영수증 발행 대기 목록에 남습니다.
                  </span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* 하향: 차액 토스 부분취소 환불 */}
        {selectedItem && isDowngrade && (
          <div className="space-y-2 rounded-md border border-orange-300 p-3 text-sm">
            <p className="font-bold text-orange-700">차액 환불 (토스 부분취소)</p>
            <p>
              현재 상품가 {currentItemTotal.toLocaleString()}원 − 새 상품가{' '}
              {selectedItemTotal.toLocaleString()}원 ={' '}
              <span className="font-bold">환불 {refundAmount.toLocaleString()}원</span>
            </p>
            <p className="text-xs text-gray-600">
              환불 후 현금결제액 {won(paymentByCash)} →{' '}
              {won(paymentByCash - refundAmount)}
              {(order.paymentDetail.paymentByPoint ?? 0) > 0
                ? ` (포인트 ${won(order.paymentDetail.paymentByPoint)}은 그대로)`
                : ''}
              . 실도매(정산 기준) {won(currentEmbed?.priceWhole)} →{' '}
              {won(actualWholesaleOf(selectedItem))}.
            </p>

            {refundBlockReason ? (
              <p className="break-keep rounded-md border border-red-300 bg-red-50 p-2 text-red-600">
                {refundBlockReason}
              </p>
            ) : (
              <>
                <p className="break-keep rounded-md bg-orange-50 p-2 text-xs text-orange-800">
                  {isVbank
                    ? '가상계좌 결제건입니다 — 환불받을 계좌를 함께 보내면 토스가 그 계좌로 입금합니다(영업일 1~2일).'
                    : `${
                        order.paymentDetail.method ?? '카드'
                      } 결제건입니다 — 원결제수단으로 환불됩니다(카드사 기준 3~7일). 환불 경로는 결제수단에 따라 자동으로 정해집니다.`}{' '}
                  구매자에게 상품 변경·환불 안내 문자가 발송됩니다.
                </p>
                <div className="flex items-center gap-2">
                  <label className="w-20 shrink-0 font-medium">교체 사유</label>
                  <input
                    type="text"
                    className="w-full rounded border border-gray-300 px-2 py-1"
                    placeholder="ex. 5단 재고 소진 (토스 취소사유·주문 메모에 남습니다)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                {isVbank && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="w-20 shrink-0 font-medium">은행</label>
                      <select
                        className="w-full rounded border border-gray-300 bg-white px-2 py-1"
                        value={bank}
                        onChange={(e) => setBank(e.target.value)}
                      >
                        <option value="">은행 선택</option>
                        {bankList.map((option) => (
                          <option key={option.code} value={option.name}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="w-20 shrink-0 font-medium">계좌번호</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="w-full rounded border border-gray-300 px-2 py-1"
                        placeholder="- 없이 입력해도 됩니다"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="w-20 shrink-0 font-medium">예금주</label>
                      <input
                        type="text"
                        className="w-full rounded border border-gray-300 px-2 py-1"
                        value={holderName}
                        onChange={(e) => setHolderName(e.target.value)}
                      />
                    </div>
                    <p className="break-keep text-xs text-gray-500">
                      예금주는 통장에 적힌 이름 그대로 — 토스가 실예금주와 대조해 다르면
                      거절합니다.
                    </p>
                  </>
                )}
                <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={refundConfirmed}
                    onChange={(e) => setRefundConfirmed(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="break-keep">
                    <b>{refundAmount.toLocaleString()}원</b>이 즉시 환불되며 되돌릴 수
                    없음을 확인했습니다.
                  </span>
                </label>
              </>
            )}
          </div>
        )}

        <Button
          className={`button-rectangle min-h-[40px] theme-text-4 ${
            isDowngrade ? 'bg-orange-600' : 'theme-bg-main'
          }`}
          disabled={submitDisabled}
          onClick={onSubmit}
        >
          {submitting
            ? '처리 중...'
            : isDowngrade
            ? `상품 교체 + ${refundAmount.toLocaleString()}원 환불`
            : '상품 교체'}
        </Button>
      </div>
    </Modal>
  );
};
